import type {AccessCodeEntryMode, CheckoutConfig, HowToEnrollContent, HowToStep} from '../types';

/** How the flow opens: typed code, location picker, or no gate at all. */
export type HowToGate = AccessCodeEntryMode | 'none';

/**
 * Shared "How to enroll" copy. The tokens below are replaced at render time from the group's actual
 * products/plans (single source of truth) so the copy can't list coverage a group doesn't offer.
 * A site without its own copy gets the variant matching its current gate (siteHowToEnroll).
 */
export const MEDICAL_PLANS_TOKEN = '{medicalPlans}';
export const OTHER_BENEFITS_TOKEN = '{otherBenefits}';
/** Step 3 title, e.g. "dental and vision" or "dental, vision, and other benefits". */
export const OTHER_BENEFITS_TITLE_TOKEN = '{otherBenefitsTitle}';
/** "Each is optional…" for several benefits, "It's optional…" for one. */
export const OPTIONAL_NOTE_TOKEN = '{optionalNote}';
/** "medical", or "limited medical" on sites without regular medical. */
export const MEDICAL_KIND_TOKEN = '{medicalKind}';
/** The site's pay period as an adjective: "weekly", "bi-weekly", "semi-monthly", "monthly". */
export const PAY_PERIOD_TOKEN = '{payPeriod}';
/** Step 2's sentence, worded for one plan or several (see medicalChoiceSentence). */
export const MEDICAL_CHOICE_TOKEN = '{medicalChoice}';
/** Replaced at render with the group's access-code term (e.g. "AxisCare Code"); defaults to "access code". */
/** `{contact}` is replaced at render with the group's benefitsContact; defaults to "HR office". */
export const ACCESS_CODE_TERM_TOKEN = '{accessCodeTerm}';

const HOW_TO_SUBTITLE =
  "Getting started is easy. The whole process takes about 5 minutes from start to finish. Here's what to expect.";

// Only for code-gated groups: find and enter your location's access code.
const ACCESS_CODE_STEP: HowToStep = {
  title: `Find your location ${ACCESS_CODE_TERM_TOKEN}`,
  body: `Your {contact} has a five-digit ${ACCESS_CODE_TERM_TOKEN} for your location. If you don't have it, ask your {contact} or call the Concierge Team.`,
  tip: `Tip: every branch has its own ${ACCESS_CODE_TERM_TOKEN} — your code controls which plans you see and your pricing tier.`,
};

// Dropdown-mode opener — swapped in for ACCESS_CODE_STEP at render, so a site that later
// switches modes doesn't need its stored how-to copy rewritten.
export const LOCATION_DROPDOWN_STEP: HowToStep = {
  title: 'Find your location',
  body: "Pick your location from the list. If you don't see it, ask your {contact} or call the Concierge Team.",
  tip: 'Tip: your location controls which plans you see and your pricing tier.',
};

/** Any step that talks about the code is the code step; in dropdown mode it becomes the picker step. */
export function resolveHowToSteps(
  steps: HowToStep[],
  gate: HowToGate,
  hasOtherBenefits = true,
): HowToStep[] {
  const mentions = (step: HowToStep, token: string) =>
    [step.title, step.body, step.tip ?? ''].some((text) => text.includes(token));
  // A group selling only medical has nothing for the "add other benefits" step to list.
  const offered = hasOtherBenefits
    ? steps
    : steps.filter((step) => !mentions(step, OTHER_BENEFITS_TOKEN));
  if (gate !== 'dropdown') return offered;
  return offered.map((step) => (mentions(step, ACCESS_CODE_TERM_TOKEN) ? LOCATION_DROPDOWN_STEP : step));
}

/** Step 2's sentence: compare several plans, or review the only one. */
export function medicalChoiceSentence(planNames: string[]): string {
  if (planNames.length === 1) {
    const name = planNames[0];
    return `Review the ${name}${/\bplan$/i.test(name) ? '' : ' plan'} to see if it fits your needs.`;
  }
  const list = new Intl.ListFormat('en', {style: 'long', type: 'conjunction'}).format(planNames);
  return `Compare ${list} side-by-side, then choose the plan that best fits your needs.`;
}

/** Token values for the benefits offered past the medical step (labels in flow order). */
export function otherBenefitsTokens(labels: string[]): Record<string, string> {
  const and = new Intl.ListFormat('en', {style: 'long', type: 'conjunction'});
  const or = new Intl.ListFormat('en', {style: 'long', type: 'disjunction'});
  return {
    [OTHER_BENEFITS_TOKEN]: or.format(labels),
    [OTHER_BENEFITS_TITLE_TOKEN]:
      labels.length > 2 ? `${labels[0]}, ${labels[1]}, and other benefits` : and.format(labels),
    [OPTIONAL_NOTE_TOKEN]:
      labels.length > 1
        ? "Each is optional — skip what you don't want."
        : "It's optional — skip it if you don't want it.",
  };
}

// The public-group opener, in place of the access-code step.
const GET_STARTED_STEP: HowToStep = {
  title: 'Get started',
  body: 'Click Enroll Now to begin your enrollment — no login or account needed.',
};

// The steps every group shares, whether or not a code gates the flow.
const SHARED_HOW_TO_STEPS: HowToStep[] = [
  {
    title: `Choose your ${MEDICAL_KIND_TOKEN} plan`,
    body: MEDICAL_CHOICE_TOKEN,
  },
  {
    title: `Add ${OTHER_BENEFITS_TITLE_TOKEN}`,
    body: `You can also enroll in ${OTHER_BENEFITS_TOKEN}. ${OPTIONAL_NOTE_TOKEN}`,
  },
  {
    title: 'Review your cart',
    body: `See your total ${PAY_PERIOD_TOKEN} cost and what'll come out of each paycheck. Make changes here if you change your mind.`,
  },
  {
    title: 'Enter your info and any dependents',
    body: "Provide your name, date of birth, Social Security number, and contact info. Add your spouse and any children you're enrolling.",
  },
  {
    title: "Submit and you're done",
    body: "You'll get a confirmation number and an email receipt. Your benefits go live on your group's coverage start date — your {contact} confirms the exact date.",
  },
];

export const HOW_TO_ENROLL_WITH_CODE: HowToEnrollContent = {
  subtitle: HOW_TO_SUBTITLE,
  steps: [ACCESS_CODE_STEP, ...SHARED_HOW_TO_STEPS],
};

/** Public groups (no access code) — a "get started" opener in place of the code step. */
export const HOW_TO_ENROLL_NO_CODE: HowToEnrollContent = {
  subtitle: HOW_TO_SUBTITLE,
  steps: [GET_STARTED_STEP, ...SHARED_HOW_TO_STEPS],
};

/** Omitted = the shared copy for how the site enrolls today, so a gate change carries over. */
export function siteHowToEnroll(
  content: HowToEnrollContent | undefined,
  gate: HowToGate,
): HowToEnrollContent {
  return content ?? (gate === 'none' ? HOW_TO_ENROLL_NO_CODE : HOW_TO_ENROLL_WITH_CODE);
}

/**
 * "What you'll need before you start" checklist. Mirrors the mock's items while staying honest
 * about what the checkout step collects: full SSN (subscriber + each dependent), and the
 * config-driven bits — the location code or location only when the group gates on one, date of hire only when
 * collected, and the configured phone label.
 */
export function buildHowToChecklist(
  checkout: CheckoutConfig | undefined,
  gate: HowToGate,
): string[] {
  const phone = (checkout?.phoneLabel ?? 'Phone').toLowerCase();
  const locationItem =
    gate === 'code'
      ? [`Your five-digit location ${ACCESS_CODE_TERM_TOKEN}`]
      : gate === 'dropdown'
        ? ['Your work location']
        : [];
  return [
    ...locationItem,
    'Your date of birth',
    'Your Social Security number',
    `Email address and ${phone} number`,
    ...(checkout?.collectDateOfHire ? ['Your date of hire'] : []),
    'Your home address and ZIP',
    "Name, date of birth, and Social Security number for any dependents you're enrolling",
  ];
}
